import type { MarkAreaComponentOption, MarkLineComponentOption } from 'echarts';
import {
  ChartsTheme,
  EChartsDataFormat,
  LOG_SCALE_AXIS_NEAR_ZERO_NUMBER,
  TableLegendPinnedColumns,
} from '@/components';
import {
  AGGREGATIONS_CONFIG,
  formatValue,
  getCommonTimeScale,
  isLogScale,
  isPercentStackEnabled,
  isStackEnabled,
  LegendSpecOptions,
  LineStyleOverrideType,
  StackOptions,
  ThresholdColorPalette,
  TIME_SERIES_HIDE_AREA_OPACITY_LIMIT,
  TIME_SERIES_HIDE_POINTS_LIMIT,
  TIME_SERIES_SPARSE_SERIES_RENDER_LIMIT,
  TimeScale,
  TimeSeriesChartStepOption,
  TimeSeriesChartThresholdOptions,
  TimeSeriesChartVisualOptions,
  TimeSeriesChartYAxisOptions,
  TimeSeriesDataset,
  TimeSeriesOption,
  TimeSeriesValueTuple,
  UnitOptions,
} from '@/core';
import {
  DIFF_BAND_TRUNCATION_SERIES_NAME,
  LOWER_BAND_TRUNCATION_SERIES_NAME,
  UPPER_BAND_TRUNCATION_SERIES_NAME,
} from '@/core/utils/truncation';
import {
  TABLE_CELL_WITH_LABEL_FILTER_BUTTONS_MIN_WIDTH,
  TABLE_CELL_WITH_LABEL_MIN_WIDTH,
  TableCellWithLabelFilterButtons,
  UseDataQueryResult,
  useDeprecatedDataQueries,
} from '@/plugin-system';
import { TableLegendColumns } from '../../../../components/Legend/TableLegend';
import {
  DEFAULT_BAR_CATEGORY_GAP,
  DEFAULT_CONNECT_NULLS,
  DEFAULT_LINE_WIDTH,
  DEFAULT_POINT_RADIUS,
  DEFAULT_UNIT,
  DEFAULT_Y_AXIS,
  POINT_SIZE_OFFSET,
  TimeSeriesChartExtendedYAxisOptions,
  TimeSeriesChartProps,
  TimeSeriesChartYAxisComponentOption,
} from '../time-series-chart-model';
import { findNiceMinMax } from './niceMinMax';

export type RunningQueriesState = ReturnType<typeof useDeprecatedDataQueries>;

export const EMPTY_GRAPH_DATA: EChartsDataFormat = {
  timeSeries: [],
  xAxis: [],
  legendItems: [],
};

export const LEGACY_HIDE_DATAPOINTS_LIMIT = 70;

// Adjusts opacity of lines not close to cursor, only works when visual.area_opacity is 0 to avoid flicker issue.
export const BLUR_FADEOUT_OPACITY = 1;

// https://echarts.apache.org/en/option.html#series-line.markArea.label.padding
const THRESHOLD_LABEL_PADDING_PX = [2, 3];

/**
 * ECharts defaults precision to 2 which can cause markLine / markArea to be plotted incorrectly.
 * 10 is chosen arbitrarily since increasing further will not impact where the line is plotted visually.
 * https://echarts.apache.org/en/option.html#series-line.markLine.precision
 */
const THRESHOLD_MARK_LINE_PRECISION = 10;

const EMPTY_MARK_LINE_DATA: MarkLineComponentOption['data'] = [];
const EMPTY_MARK_AREA_DATA: MarkAreaComponentOption['data'] = [];

const LINE_SERIES_TYPE_TO_ECHARTS_LINE_SERIES_TYPE: Record<LineStyleOverrideType, 'dashed' | 'solid'> = {
  ['Dashed']: 'dashed',
  ['Solid']: 'solid',
};

/**
 * Given a list of running queries, calculates a common time scale for use on
 * the x axis (i.e. start/end dates and a step that is divisible into all of
 * the queries' steps).
 */
export function getCommonTimeScaleForQueries(
  queries: UseDataQueryResult<'TimeSeries'>['queryResults']
): TimeScale | undefined {
  const seriesData = queries.map((query) => (query.isLoading ? undefined : query.data));
  return getCommonTimeScale(seriesData);
}

/**
 * Get the time scale that is used when one is not available.
 */
export function getFallbackTimeScale(): TimeScale {
  const today = new Date();
  const pastDate = new Date(today);
  pastDate.setFullYear(today.getFullYear() - 5);
  const todayMs = today.getTime();
  const pastDateMs = pastDate.getTime();
  return { startMs: pastDateMs, endMs: todayMs, stepMs: 1, rangeMs: todayMs - pastDateMs };
}

/**
 * Gets ECharts line series option properties for recommended TimeChart
 */
export function getTimeSeries({
  id,
  datasetIndex,
  formattedSeriesName,
  visual,
  seriesColor,
  lineStyle,
  lineWidth,
  nonconnectedDataPoints,
  sparseSeriesCount = 0,
  totalDatapoints,
  totalSeries,
  onRightYAxis = false,
  isComparedSeries = false,
  barCategoryGap,
  queryTruncationSeriesLimit,
  shouldShowAllDataPoints = false,
  chartsTheme,
}: {
  id: string;
  datasetIndex: number;
  formattedSeriesName: string;
  visual: TimeSeriesChartVisualOptions;
  seriesColor: string;
  lineStyle?: LineStyleOverrideType;
  lineWidth?: number;
  nonconnectedDataPoints: TimeSeriesValueTuple[];
  sparseSeriesCount?: number;
  totalSeries: number;
  totalDatapoints: number;
  onRightYAxis?: boolean;
  isComparedSeries?: boolean;
  barCategoryGap?: number;
  queryTruncationSeriesLimit?: number;
  shouldShowAllDataPoints?: boolean;
  chartsTheme: ChartsTheme;
}): TimeSeriesOption {
  const { name } = processSeriesIdentifiers(id);

  const stackEnabled = visual.stack === 'All' || visual.stack === 'Percent';
  const stackAxisId = onRightYAxis ? 'RightAxis' : 'LeftAxis';
  const stackCompareId = isComparedSeries ? 'Compared' : '';
  const stackId = `${stackAxisId}${stackCompareId}`;

  // Options that are specific to bar chart variant
  if (visual.display === 'bar') {
    const series: TimeSeriesOption = {
      type: 'bar',
      id,
      datasetIndex,
      isComparison: isComparedSeries,
      name,
      formattedSeriesName,
      yAxisIndex: onRightYAxis ? 1 : 0, // Enables multi y axis support
      color: seriesColor,
      stack: stackEnabled ? stackId : undefined,
      label: {
        show: false,
      },
      barCategoryGap: barCategoryGap ?? DEFAULT_BAR_CATEGORY_GAP,
    };
    if (isComparedSeries) {
      series.itemStyle = { decal: chartsTheme.stripedDecal };
    }
    return series;
  }

  // Line chart options
  const width = lineWidth ?? visual.line_width ?? DEFAULT_LINE_WIDTH;
  let pointRadius = visual.line_width !== undefined ? visual.line_width + POINT_SIZE_OFFSET : DEFAULT_POINT_RADIUS;
  // Increase symbol size further when points are not connected
  const isSparseSeries = nonconnectedDataPoints.length > 0;
  if (isSparseSeries) {
    pointRadius += POINT_SIZE_OFFSET;
  }

  // Gives control over when symbols render but fallback to automatic behavior that is best for performance.
  const showPointsMode = shouldShowPoints({
    visual,
    totalDatapoints,
    nonconnectedDataPoints,
    sparseSeriesCount,
    shouldShowAllDataPoints,
  });

  // Turn off area_opacity when many series to optimize performance
  const isOptimizedAreaOpacityEnabled = totalSeries > TIME_SERIES_HIDE_AREA_OPACITY_LIMIT && !stackEnabled;
  const isAreaOpacityPopulated =
    !isOptimizedAreaOpacityEnabled && visual.area_opacity !== undefined && visual.area_opacity > 0;

  // Account for line style override
  const lineStyleType = lineStyle !== undefined ? LINE_SERIES_TYPE_TO_ECHARTS_LINE_SERIES_TYPE[lineStyle] : undefined;

  const baseSymbolSize = visual.area_opacity ? pointRadius + 0.5 : pointRadius;

  // For sparse series, only render symbols at nonconnected (isolated) data points
  // instead of every vertex.
  let symbolSize: number | ((value: [number, number | null]) => number) = baseSymbolSize;
  if (showPointsMode === 'sparse-only') {
    const nonconnectedTimestamps = new Set(nonconnectedDataPoints.map(([ts]) => ts));
    symbolSize = (value: [number, number | null]) => {
      return nonconnectedTimestamps.has(value[0]) ? baseSymbolSize : 0;
    };
  }

  const series: TimeSeriesOption = {
    type: 'line',
    id,
    datasetIndex,
    isComparison: isComparedSeries,
    name,
    formattedSeriesName,
    yAxisIndex: onRightYAxis ? 1 : 0, // Enables multi y axis support
    // TODO: connect_nulls left for backwards compatibility, clean up when schema migration is ready
    connectNulls: (visual.connect_nulls || visual.null_behavior === 'Connected') ?? DEFAULT_CONNECT_NULLS,
    color: seriesColor,
    stack: stackEnabled ? stackAxisId : undefined,
    symbol: 'circle', // Better performance than default emptyCircle
    showSymbol: showPointsMode !== 'none',
    showAllSymbol: true,
    symbolSize,
    triggerLineEvent: true, // Important so mouseover event fires in TimeChartBase
    lineStyle: {
      width: width,
      opacity: 1,
      type: isComparedSeries ? 'dashed' : lineStyleType,
    },
    // https://echarts.apache.org/en/option.html#series-line.emphasis
    emphasis: {
      lineStyle: {
        width: width + 1,
      },
    },
  };

  /**
   * QUERY Truncation styling
   */
  // If the series is a top or bottom band, adjust the line style
  // TODO: Move this to a first class property instead of checking the series name
  // [sc-129785] https://app.shortcut.com/chronosphere/story/129785
  const seriesName = series.name?.toString() ?? '';
  if (series.name && series.type === 'line') {
    if (!series.lineStyle) {
      series.lineStyle = {};
    }

    // Only add area style to the diff between lower and upper bands
    if (seriesName.includes(DIFF_BAND_TRUNCATION_SERIES_NAME)) {
      series.stack = 'truncated';
      series.z = -1; // Use a lower z-level to render shaded area behind regular series
      series.areaStyle = {
        color: chartsTheme.confidenceBandColor,
        opacity: 0.3,
      };
    } else if (seriesName.includes(LOWER_BAND_TRUNCATION_SERIES_NAME)) {
      series.stack = 'truncated';
      series.lineStyle.width = 2;
      series.lineStyle.type = 'dashed';
    } else if (seriesName.includes(UPPER_BAND_TRUNCATION_SERIES_NAME)) {
      // Do not stack the upper band so that correct value is displayed in tooltip
      series.lineStyle.width = 2;
      series.lineStyle.type = 'dashed';
    }
  }

  // Important to not set areaStyle unless explicitly defined or mouseover event in TimeChartBase is less accurate
  if (isAreaOpacityPopulated) {
    series.areaStyle = {
      opacity: visual.area_opacity,
    };
  }

  /**
   * Controls how many canvas instances are used per panel, helps with render time and crosshair responsiveness.
   * Using query truncation limit allows all new series after opt-in to be plotted separately on a different canvas.
   * Note: ECharts already uses a zlevel of 100000 as a highlighting layer when hovering over individual lines.
   * https://echarts.apache.org/en/option.html#series-line.zlevel
   */
  if (queryTruncationSeriesLimit !== undefined && datasetIndex >= queryTruncationSeriesLimit) {
    series.zlevel = 1;
  }

  return series;
}

/**
 * Sets the `name` and `displayName` properties for a TimeSeriesOption.
 * The `name` is set to `id` intentionally as a workaround since id is not returned in mouseover event params.
 */
export function processSeriesIdentifiers(id: string) {
  return {
    name: id,
  };
}

/**
 * Gets threshold-specific series that is used to attach markLine and markArea data.
 * Only one threshold series is added to series mapping but stores many step values.
 */
export function getThresholdsSeries({
  thresholds,
  thresholdColors,
  name,
  dataset,
  yAxisMax,
}: {
  thresholds: TimeSeriesChartThresholdOptions;
  thresholdColors: ThresholdColorPalette;
  name: string;
  dataset: TimeSeriesDataset[];
  yAxisMax?: number;
}): TimeSeriesOption | undefined {
  if (!thresholds.steps || thresholds.steps.length === 0) return;

  // Initialize line series that will be used to attach thresholds (dashed line or area style) to.
  const series: TimeSeriesOption = {
    type: 'line',
    name: name,
    markArea: { data: [], precision: THRESHOLD_MARK_LINE_PRECISION },
    markLine: { data: [], precision: THRESHOLD_MARK_LINE_PRECISION },
    data: [], // Pass empty data to ensure series colors are not thrown off by undefined datasetIndex
  };

  // Percent mode thresholds require additional processing to determine max.
  const isPercentMode = thresholds.mode === 'Percent';
  let maxValue = yAxisMax ?? 1;
  if (isPercentMode && yAxisMax === undefined) {
    maxValue = findMax(dataset);
  }

  // Sort the steps based on value since they can be stored in spec unordered.
  const sortedSteps: TimeSeriesChartStepOption[] = [...thresholds.steps].sort((a, b) => a.value - b.value);
  sortedSteps.forEach((step, index, array) => {
    const nextStep = array[index + 1];
    const endValue = calculateStepEndValue({ isPercentMode, maxValue, nextStep });
    const stepValue = isPercentMode ? (maxValue * step.value) / 100 : step.value;
    const stepPaletteColor = thresholdColors.palette[index] ?? thresholdColors.defaultColor;

    if (step.area_opacity !== undefined && step.area_opacity > 0) {
      const areaStyleStep = getThresholdMarkAreaData(stepValue, step, thresholdColors, stepPaletteColor, endValue);
      if (areaStyleStep) {
        series.markArea?.data?.push(...areaStyleStep);
      }
    } else {
      const dashedLineStyleStep = getThresholdMarkLineData(stepValue, step, thresholdColors, stepPaletteColor);
      if (dashedLineStyleStep) {
        series.markLine?.data?.push(...dashedLineStyleStep);
      }
    }
  });

  return series;
}

/**
 * Find max value in ECharts dataset, used to determine end value for Percent mode thresholds
 */
function findMax(dataset: TimeSeriesDataset[]) {
  let max = 0;
  if (dataset.length && dataset[0] !== undefined && dataset[0].source) {
    dataset.forEach((series) => {
      series.source.forEach((sourceEntry) => {
        const [_, value] = sourceEntry;
        if (typeof value === 'number' && value > max) {
          max = value;
        }
      });
    });
  }
  return max;
}

/**
 * Converts Perses panel y_axis from dashboard spec to ECharts supported yAxis options.
 * Set the y axis min and max relative to the data, while accounting for threshold.scale_axis (set in monitors).
 */
export function convertPanelYAxis(
  inputAxis: TimeSeriesChartYAxisOptions = {},
  thresholds?: TimeSeriesChartThresholdOptions,
  stack?: StackOptions,
  /** When true, stacked charts use auto min instead of pinning to 0. */
  enableAutoScaleStackedMin = false
): TimeSeriesChartExtendedYAxisOptions {
  // When no custom axis max is set and Percent stack is used, set axis max to 100.
  const isPercentStack = isPercentStackEnabled(stack);
  let max = inputAxis?.max;
  if (inputAxis?.max === undefined && isPercentStack) {
    max = 100;
  }

  const yAxis: TimeSeriesChartExtendedYAxisOptions = {
    type: isLogScale(inputAxis.scale) ? 'log' : 'value',
    show: inputAxis?.show ?? DEFAULT_Y_AXIS.show,
    min: inputAxis?.min,
    max,
    splitNumber: inputAxis.splitNumber,
    unit: inputAxis.unit ?? DEFAULT_UNIT,
  };

  // Adjust the min value considering both data and thresholds
  if (yAxis.min === undefined) {
    yAxis.min = (value) => {
      let minValue = value.min;
      let maxValue = value.max;

      // Ensure results with mixed signs allows the min to go below zero (sc-141080).
      // Leave auto min behavior when log scale axis is used so values are not compressed disproportionately.
      // enableAutoScaleStackedMin flag skips pinning to 0 for auto-scaled stacked mins,
      // but Percent stacks always pin to 0 so the 0–100 axis is not distorted.
      const isStack = isStackEnabled(stack);
      if ((!enableAutoScaleStackedMin || isPercentStack) && isStack && minValue > 0 && yAxis.type !== 'log') {
        minValue = 0;
      }

      // Adjust yAxis min so thresholds are always visible by default
      if (thresholds?.steps?.length && thresholds.scale_axis !== false) {
        minValue = thresholds.steps.reduce((acc, step) => Math.min(acc, step.value), minValue);
        maxValue = thresholds.steps.reduce((acc, step) => Math.max(acc, step.value), maxValue);
      }

      const [min] = findNiceMinMax({ minValue, maxValue, hardMin: inputAxis.min, hardMax: inputAxis.max });

      if (yAxis.type === 'log' && min === 0) {
        if (value.min === LOG_SCALE_AXIS_NEAR_ZERO_NUMBER) {
          // This is our zero number, we don't really know what the lowest non zero number is
          // so we are just going to set it to .1
          return 0.1;
        }

        return Math.pow(10, Math.floor(Math.log10(minValue)));
      }
      return min as number;
    };
  }

  // Adjust the max value considering both data and thresholds
  if (yAxis.max === undefined) {
    yAxis.max = (value) => {
      let minValue = value.min;
      let maxValue = value.max;

      // Adjust yAxis max so thresholds are always visible by default
      if (thresholds?.steps?.length && thresholds.scale_axis !== false && thresholds.mode !== 'Percent') {
        minValue = thresholds.steps.reduce((acc, step) => Math.min(acc, step.value), minValue);
        maxValue = thresholds.steps.reduce((acc, step) => Math.max(acc, step.value), maxValue);
      }

      const [, max] = findNiceMinMax({ minValue, maxValue, hardMin: inputAxis.min, hardMax: inputAxis.max });
      return max as number;
    };
  }

  // Correct axis if log scale
  if (yAxis.type === 'log') {
    if (inputAxis.scale === 'Log2') {
      yAxis.logBase = 2;
    }

    if (yAxis.min === 0) {
      yAxis.min = 1;
    }
  }

  return yAxis;
}

/**
 * Modifies the y-axis configuration to show the most reasonable y-axis we can when there is no data
 * available to infer min/max values.
 */
export function calculateNoDataYAxis(
  originalYAxis: TimeSeriesChartYAxisComponentOption[]
): TimeSeriesChartYAxisComponentOption[] {
  return originalYAxis.map((yAxis) => {
    const { min, max, show } = yAxis;

    // `yAxis.show` defaults to `true`, so it must be explicitly false to be hidden.
    // https://echarts.apache.org/en/option.html#yAxis.show
    const isHidden = show === false;

    if (isHidden) {
      // No changes needed if the y-axis is not visible.
      return yAxis;
    }

    const hasStaticMin = typeof min === 'number';
    const hasStaticMax = typeof max === 'number';

    // With statically defined yAxis min/max, the chart can meaningfully show the yAxis without data.
    if (hasStaticMin && hasStaticMax) {
      return {
        ...yAxis,
        splitLine: {
          ...yAxis.splitLine,
          // Turn off the split line to avoid grid lines covering the "no data" message.
          show: false,
        },
      };
    }

    let updatedMin: number;
    let updatedMax: number;
    let interval: number | undefined;
    let showMinLabel: boolean | undefined;
    let showMaxLabel: boolean | undefined;

    // Do our best to set some sensible min/max values and associated y-axis visualization when we
    // have no data and either partial or no static min/max settings.
    if (hasStaticMin && min < 0) {
      // Show range from min to 0
      updatedMin = min;
      updatedMax = 0;
      showMaxLabel = true;
    } else if (hasStaticMin && min > 0) {
      // Show just the min because it's hard to infer a potential max without data.
      updatedMin = min;
      updatedMax = updatedMin + 1;
      interval = 1;
      showMaxLabel = false;
    } else if (hasStaticMax && max < 0) {
      // Show just the max because it's hard to infer a potential min without data.
      updatedMin = max - 1;
      updatedMax = max;
      interval = 1;
      showMinLabel = false;
    } else if (hasStaticMax && max > 0) {
      // Show range from 0 to max
      updatedMin = 0;
      updatedMax = max;
      showMaxLabel = true;
    } else {
      // Show just 0 because it's hard to infer a min/max without data or just the min/max were set
      // to zero, so it's hard to infer the matching min/max without data.
      updatedMin = 0;
      updatedMax = 1;
      interval = 1;
      showMaxLabel = false;
    }

    return {
      ...yAxis,
      min: updatedMin,
      max: updatedMax,
      interval: interval,
      axisLabel: {
        ...yAxis.axisLabel,
        showMinLabel: showMinLabel,
        showMaxLabel: showMaxLabel,
      },
      splitLine: {
        ...yAxis.splitLine,
        // Turn off the split line to avoid a weird top line for the max of 1 and to avoid grid
        // lines covering the "no data" message.
        show: false,
      },
    };
  });
}

const EMPTY_LEGEND_COLUMNS: TableLegendColumns = [];

interface GetLegendColumnsOpts
  extends Pick<LegendSpecOptions, 'values'>,
    Pick<
      TimeSeriesChartProps,
      | 'customTableLegendColumnsStart'
      | 'customTableLegendColumnsEnd'
      | 'onAddFilters'
      | 'filterLabelColumns'
      | 'pinnedTableLegendColumns'
    > {
  unit: UnitOptions;
  /**
   * Array of label keys. When available, will be turned into columns.
   */
  labelKeys?: string[];
}

interface GetLegendColumnsResponse {
  legendColumns: TableLegendColumns;
  pinnedTableLegendColumns?: TableLegendPinnedColumns;
}

/**
 * Converts values into columns for display in a table legend.
 */
export function getLegendColumns({
  values,
  unit,
  labelKeys,
  customTableLegendColumnsStart = EMPTY_LEGEND_COLUMNS,
  customTableLegendColumnsEnd = EMPTY_LEGEND_COLUMNS,
  onAddFilters,
  filterLabelColumns,
  pinnedTableLegendColumns: initPinnedTableLegendColumns,
}: GetLegendColumnsOpts): GetLegendColumnsResponse {
  // Get fields for custom columns to make sure we do not have duplicates.
  // customTableLegendColumnsStart is still necessary in this method because it is used to determine if a label is overridden by a custom column,
  // even though it is not included in the legendColumns array.
  const customColumnFields = new Set([
    ...customTableLegendColumnsStart.map((col) => col.field),
    ...customTableLegendColumnsEnd.map((col) => col.field),
  ]);

  const labelColumns: TableLegendColumns = [];
  labelKeys?.forEach((label) => {
    const field = `data.${label}`;

    const overriddenByCustomColumn = customColumnFields.has(field);
    const filteredColumn = filterLabelColumns && !filterLabelColumns(label);

    if (!overriddenByCustomColumn && !filteredColumn) {
      labelColumns.push({
        field,
        headerName: label,
        valueGetter: ({ row }) => row.data?.[label],
        // For now, attempting to flexibly fill space, when possible. When the table is bigger than the space,
        // this will fall back to a stable width.
        // TODO: look into using column autosizing to see if we can reliably and performantly get a nicer
        // layout. https://v6.mui.com/x/react-data-grid/column-dimensions/#autosizing
        flex: 1,
        renderCell: ({ value }) => {
          // If we do not have filters, just return the value and let the default datatable rendering do its thing.
          if (!onAddFilters) {
            return value;
          }
          return <TableCellWithLabelFilterButtons onAddFilters={onAddFilters} labelName={label} labelValue={value} />;
        },
        // Use recommended min width when the filter buttons are shown to ensure the content and buttons are viewable
        // and do not get cut off.
        minWidth: onAddFilters ? TABLE_CELL_WITH_LABEL_FILTER_BUTTONS_MIN_WIDTH : TABLE_CELL_WITH_LABEL_MIN_WIDTH,
      });
    }
  });

  const hasValueColumns = !!values?.length;
  const hasLabelColumns = !!labelKeys?.length;
  const shouldPinValuesColumns = initPinnedTableLegendColumns === 'auto' && hasValueColumns && hasLabelColumns;

  let pinnedTableLegendColumns: TableLegendPinnedColumns | undefined;
  if (initPinnedTableLegendColumns === 'auto') {
    if (shouldPinValuesColumns) {
      // The "values" are always pinned on the right. Initializing the array, so we can populate it
      // in the values processing below.
      pinnedTableLegendColumns = {
        right: [],
      };
    }
  } else if (initPinnedTableLegendColumns === 'none') {
    // No custom pinned columns when set to none.
    pinnedTableLegendColumns = undefined;
  } else if (initPinnedTableLegendColumns) {
    // Otherwise, fall back to the config because it is a fully custom pinning configuration.
    pinnedTableLegendColumns = initPinnedTableLegendColumns;
  }

  const valuesColumns: TableLegendColumns =
    values?.reduce((columns, legendValue) => {
      const legendConfig = AGGREGATIONS_CONFIG[legendValue];
      const field = `data.${legendValue}`;

      // Do not include label field if it is overridden by a custom column.
      if (!customColumnFields.has(field) && legendConfig) {
        const field = `data.${legendValue}`;

        columns.push({
          field,
          headerName: legendConfig.label,
          description: legendConfig.description,
          // Intentionally hardcoding a column width to start based on discussions
          // with design around keeping this simple to start. This may need
          // revisiting in the future to handle edge cases with very large values.
          width: 72,
          headerAlign: 'right',
          align: 'right',
          type: 'number',
          renderCell: ({ value, row: { unit: seriesUnit } }) => {
            const cellValue = value;
            const unitValue = seriesUnit || unit;
            return typeof cellValue === 'number' && unit ? formatValue(cellValue, unitValue) : cellValue;
          },
        });

        if (shouldPinValuesColumns) {
          pinnedTableLegendColumns?.right?.push(field);
        }
      }

      return columns;
    }, [] as TableLegendColumns) ?? EMPTY_LEGEND_COLUMNS;

  // The customTableLegendColumnsStart are not included in this because they are injected in
  // the TableLegend component before the name column.
  const legendColumns = [...labelColumns, ...valuesColumns, ...customTableLegendColumnsEnd];

  return {
    legendColumns,
    pinnedTableLegendColumns,
  };
}

/**
 * Helper that returns the end value for a threshold step accounting for Percent vs. Absolute mode.
 */
export function calculateStepEndValue({
  isPercentMode,
  maxValue,
  nextStep,
}: {
  isPercentMode: boolean;
  maxValue: number;
  nextStep?: TimeSeriesChartStepOption;
}) {
  if (!nextStep) return undefined;
  return isPercentMode ? (maxValue * nextStep.value) / 100 : nextStep.value;
}

/**
 * ECharts markLine is used for default threshold steps when area_opacity is undefined or 0.
 * https://echarts.apache.org/en/option.html#series-line.markLine
 */
export function getThresholdMarkLineData(
  stepValue: number,
  step: TimeSeriesChartStepOption,
  thresholdColors: ThresholdColorPalette,
  stepPaletteColor?: string
): MarkLineComponentOption['data'] {
  const isStepNameDefined = step.name !== undefined && step.name !== '';
  if (stepValue === null) return EMPTY_MARK_LINE_DATA;
  return [
    {
      yAxis: stepValue,
      lineStyle: {
        color: step.color ?? stepPaletteColor,
        type: 'dashed',
        width: 1.25,
      },
      label: {
        show: false,
        formatter: step.name ?? '',
        color: thresholdColors.labelColor,
        backgroundColor: thresholdColors.labelBackgroundColor,
        padding: isStepNameDefined ? THRESHOLD_LABEL_PADDING_PX : 0,
        position: 'insideStartTop',
        distance: [0, 1],
      },
      emphasis: {
        lineStyle: {
          width: 1.5,
        },
        // https://echarts.apache.org/en/option.html#series-line.markLine.emphasis.label
        label: {
          show: true,
        },
      },
    },
  ];
}

/**
 * ECharts markArea is used for area style threshold steps when area_opacity is greater than 0.
 * https://echarts.apache.org/en/option.html#series-line.markArea
 */
export function getThresholdMarkAreaData(
  stepValue: number,
  step: TimeSeriesChartStepOption,
  thresholdColors: ThresholdColorPalette,
  stepPaletteColor: string,
  endValue?: number
): MarkAreaComponentOption['data'] {
  const isStepNameDefined = step.name !== undefined && step.name !== '';
  if (stepValue === null) return EMPTY_MARK_AREA_DATA;
  return [
    [
      {
        yAxis: stepValue,
        itemStyle: {
          color: step.color ?? stepPaletteColor,
          opacity: step.area_opacity,
        },
        label: {
          show: false,
          formatter: step.name ?? '',
          color: thresholdColors.labelColor,
          backgroundColor: thresholdColors.labelBackgroundColor,
          padding: isStepNameDefined ? THRESHOLD_LABEL_PADDING_PX : 0,
        },
        emphasis: {
          label: {
            show: true,
            position: 'insideTopLeft',
          },
        },
      },
      { yAxis: endValue },
    ],
  ];
}

export type ShowPointsMode = 'all' | 'sparse-only' | 'none';

/**
 * Determines how datapoint symbols should render for a series.
 * - 'all': render a symbol at every data point
 * - 'sparse-only': render symbols only at nonconnected (isolated) data points
 * - 'none': do not render symbols
 */
export function shouldShowPoints({
  visual,
  totalDatapoints,
  nonconnectedDataPoints,
  sparseSeriesCount,
  shouldShowAllDataPoints,
}: {
  visual: TimeSeriesChartVisualOptions;
  totalDatapoints: number;
  nonconnectedDataPoints: TimeSeriesValueTuple[];
  sparseSeriesCount: number;
  shouldShowAllDataPoints: boolean;
}): ShowPointsMode {
  if (shouldShowAllDataPoints) {
    return 'all';
  }

  if (visual.show_points === 'Never') {
    return 'none';
  }

  if (visual.show_points === 'Always') {
    return 'all';
  }

  const isSparseSeries = nonconnectedDataPoints.length > 0;
  if (isSparseSeries && sparseSeriesCount < TIME_SERIES_SPARSE_SERIES_RENDER_LIMIT) {
    return 'sparse-only';
  }

  const isUnderHidePointsLimit = totalDatapoints > 0 && totalDatapoints < TIME_SERIES_HIDE_POINTS_LIMIT;
  if (isUnderHidePointsLimit) {
    return 'all';
  }

  return 'none';
}
